import React from 'react';
import type { LoyaltyGrade, LoyaltyProgramType } from '../../../shared/types';
import { WarningIcon } from '../icons/UiIcons';
import { GRADE_SWATCHES, gradeHex } from './ProgramPreview';

interface GradesConfigProps {
    grades: LoyaltyGrade[];
    onChange: (grades: LoyaltyGrade[]) => void;
    /** Type du programme — détermine l'unité des seuils (pts ou visites). */
    programType: LoyaltyProgramType;
    errors?: string[];
    disabled?: boolean;
}

const MAX_GRADES = 5;

/**
 * Identifiant unique d'un niveau (clé React et identifiant du niveau côté serveur) :
 * aléatoire, jamais déduit de la position — supprimer puis ajouter un niveau
 * redonnait « niveau-3 » à deux niveaux.
 */
const newGradeId = (taken: ReadonlySet<string>) => {
    for (;;) {
        const raw = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID() : `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
        const id = `grade-${raw.slice(0, 12)}`;
        if (!taken.has(id)) return id;
    }
};

/** Texte lisible (encre ou crème) selon la clarté de la couleur du niveau. */
const inkOn = (hex: string) => {
    const n = parseInt(hex.slice(1), 16);
    const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
    return (0.299 * r + 0.587 * g + 0.114 * b) > 150 ? '#3A1F20' : '#FFF5F3';
};

/**
 * Niveaux de fidélité en cartes colorées (Bronze / Argent / Or…), comme la
 * section « Des niveaux qui donnent envie de revenir » du site : nom, seuil,
 * avantages cochables, couleur.
 */
const GradesConfig: React.FC<GradesConfigProps> = ({ grades, onChange, programType, errors, disabled }) => {
    const unit = programType === 'stamps' ? 'visites' : 'points';

    const updateGrade = (idx: number, patch: Partial<LoyaltyGrade>) => {
        onChange(grades.map((g, i) => i === idx ? { ...g, ...patch } : g));
    };

    const addGrade = () => {
        if (grades.length >= MAX_GRADES) return;
        const lastThreshold = grades[grades.length - 1]?.threshold ?? 0;
        const idx = grades.length;
        onChange([
            ...grades,
            {
                id: newGradeId(new Set(grades.map((g) => g.id))),
                name: `Niveau ${idx + 1}`,
                threshold: lastThreshold + (programType === 'stamps' ? 5 : 50),
                color: GRADE_SWATCHES[idx % GRADE_SWATCHES.length].hex,
                advantages: [],
            },
        ]);
    };

    const removeGrade = (idx: number) => {
        if (idx === 0) return; // le 1er niveau est attribué par défaut
        onChange(grades.filter((_, i) => i !== idx));
    };

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 items-start">
                {grades.map((grade, gIdx) => {
                    const hex = gradeHex(grade, gIdx);
                    const ink = inkOn(hex);
                    const isFirst = gIdx === 0;
                    return (
                        <div key={grade.id} className="rounded-3xl p-4 shadow-sm" style={{ background: `linear-gradient(160deg, ${hex}cc, ${hex})`, color: ink }}>
                            <div className="flex items-center gap-2">
                                <span className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-white/70 text-xs font-bold text-gray-900">{gIdx + 1}</span>
                                <input
                                    type="text"
                                    value={grade.name}
                                    onChange={e => updateGrade(gIdx, { name: e.target.value })}
                                    disabled={disabled}
                                    placeholder="Nom du niveau"
                                    aria-label={`Nom du niveau ${gIdx + 1}`}
                                    className="min-w-0 flex-1 rounded-xl border-0 !bg-white/60 px-3 py-1.5 font-display text-lg !text-gray-900 outline-none focus:ring-2 focus:ring-brand-500 focus:!bg-white"
                                />
                                {!isFirst && (
                                    <button
                                        type="button"
                                        onClick={() => removeGrade(gIdx)}
                                        disabled={disabled}
                                        aria-label={`Supprimer le niveau ${grade.name}`}
                                        className="h-7 w-7 rounded-full bg-white/40 text-sm leading-none hover:bg-white/70 disabled:opacity-40"
                                        style={{ color: ink }}
                                    >
                                        ×
                                    </button>
                                )}
                            </div>

                            <div className="mt-3 flex items-center gap-2 text-sm">
                                {isFirst ? (
                                    <span className="opacity-80">Attribué à l'inscription</span>
                                ) : (
                                    <>
                                        <span>Dès</span>
                                        <input
                                            type="number"
                                            min={1}
                                            value={grade.threshold}
                                            onChange={e => updateGrade(gIdx, { threshold: parseInt(e.target.value) || 0 })}
                                            disabled={disabled}
                                            aria-label={`Seuil du niveau ${grade.name}`}
                                            className="w-20 rounded-lg border-0 !bg-white/60 px-2 py-1 text-center font-semibold !text-gray-900 outline-none focus:ring-2 focus:ring-brand-500 focus:!bg-white"
                                        />
                                        <span>{unit}</span>
                                    </>
                                )}
                            </div>

                            <div className="mt-3 flex items-center gap-1.5" role="radiogroup" aria-label={`Couleur du niveau ${grade.name}`}>
                                {GRADE_SWATCHES.map(sw => (
                                    <button
                                        key={sw.hex}
                                        type="button"
                                        role="radio"
                                        aria-checked={hex === sw.hex}
                                        title={sw.name}
                                        disabled={disabled}
                                        onClick={() => updateGrade(gIdx, { color: sw.hex })}
                                        className={`h-5 w-5 rounded-full border-2 transition-transform ${hex === sw.hex ? 'scale-110 border-gray-900' : 'border-white/70'}`}
                                        style={{ background: sw.hex }}
                                    />
                                ))}
                            </div>
                        </div>
                    );
                })}

                {grades.length < MAX_GRADES && (
                    <button
                        type="button"
                        onClick={addGrade}
                        disabled={disabled}
                        className="flex min-h-[160px] flex-col items-center justify-center rounded-3xl border-2 border-dashed border-gray-300 text-sm font-semibold text-gray-600 transition-colors hover:border-brand-300 hover:bg-cream hover:text-brand-600 disabled:opacity-40"
                    >
                        <span className="mb-1 text-2xl leading-none">+</span>
                        Ajouter un niveau
                        <span className="mt-1 text-xs font-normal text-gray-400">{grades.length}/{MAX_GRADES}</span>
                    </button>
                )}
            </div>

            {errors && errors.length > 0 && (
                <div className="space-y-1 rounded-xl border border-brand-200 bg-brand-50 p-3">
                    {errors.map((err, i) => (
                        <p key={i} className="inline-flex items-center gap-1.5 text-xs text-brand-700"><WarningIcon className="h-3.5 w-3.5 flex-shrink-0" /> {err}</p>
                    ))}
                </div>
            )}
        </div>
    );
};

export default GradesConfig;
